"use client";

import { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";

import { nodes } from "./stackIntegrationsData";

const HUB = { x: 50, y: 49 };

const curvePath = (node) => {
  const { left: x, top: y } = node.pos;
  const midX = (HUB.x + x) / 2;
  // S-curve: leaves the hub horizontally, arrives at the chip horizontally
  return `M ${HUB.x} ${HUB.y} C ${midX} ${HUB.y}, ${midX} ${y}, ${x} ${y}`;
};
const NodeChip = ({ label, icon }) => (
  <span className="inline-flex items-center gap-2.5 rounded-2xl bg-white px-4 py-2.5 shadow-[0_10px_30px_-12px_rgba(15,17,24,0.35)] ring-1 ring-black/5">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center [&>svg]:h-full [&>svg]:w-full">
      {icon}
    </span>
    <span className="whitespace-nowrap text-[12px] font-medium text-black 3xl:text-[13px]">
      {label}
    </span>
  </span>
);

const StackIntegrations = () => {
  const [open, setOpen] = useState(true);

  return (
    <section className="bg-white py-16  sm:pt-10 ">
      <header className="mx-auto max-w-[340px]  md:max-w-[520px] xl:max-w-[640px]  2xl:max-w-[1500px] 3xl:max-w-[1500px]  text-center">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#292929]">
          <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#7F7FFF]/20">
            <span className="block h-1.5 w-1.5 rounded-full bg-[#7F7FFF]" />
          </span>
          <span className="font-kalam normal-case">Plugs into your stack</span>
        </div>

        <h2 className="solution-sub-heading  ">
          Your entire stack
          <br className="hidden sm:block" /> connected in one place.
        </h2>

        <p className="mx-auto mt-4 max-w-[560px] 2xl:max-w-[640px] body-secondary-text">
          Imagive connects to the tools your team uses every day, so insights
          flow where you actually work.
        </p>
      </header>

      {/* ── Constellation (hub + animated chips, all breakpoints) ── */}
      <div
        style={{
          backgroundImage: " url(/assets/creativeIntelligence/dotted-bg.svg)",
          backgroundSize: "cover",
        }}
        className="relative mx-auto mt-10 flex w-full justify-center overflow-hidden h-[220px] sm:h-[370px] md:h-[470px] lg:mt-12 lg:h-[600px]"
      >
        {/* Fixed-size stage scaled down to fit narrow screens, so the
            composition looks identical (just smaller) at every width. */}
        <div className="relative h-[600px] w-[1000px] shrink-0 origin-top scale-[0.355] sm:scale-[0.6] md:scale-[0.76] lg:scale-100">
          <AnimatePresence>
            {open && (
              <svg
                key="lines"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full"
              >
                {nodes.map((n, i) => (
                  <motion.path
                    key={n.label}
                    d={curvePath(n)}
                    fill="none"
                    stroke="#6366F1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    strokeDasharray="1.6 4.5"
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 0.55,
                      transition: { duration: 0.4, delay: 0.5 + i * 0.05 },
                    }}
                    exit={{ opacity: 0, transition: { duration: 0.12 } }}
                  />
                ))}
              </svg>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {open &&
              nodes.map((n, i) => (
                <motion.div
                  key={n.label}
                  className="absolute"
                  style={{ zIndex: 1 }}
                  initial={{
                    left: `${HUB.x}%`,
                    top: `${HUB.y}%`,
                    x: "-50%",
                    y: "-50%",
                    opacity: 0,
                    scale: 0.4,
                    rotate: n.rotate,
                  }}
                  animate={{
                    left: `${n.pos.left}%`,
                    top: `${n.pos.top}%`,
                    x: "-50%",
                    y: "-50%",
                    opacity: 1,
                    scale: 1,
                    rotate: n.rotate,
                  }}
                  exit={{
                    left: `${HUB.x}%`,
                    top: `${HUB.y}%`,
                    x: "-50%",
                    y: "-50%",
                    opacity: 0,
                    scale: 0.4,
                    rotate: n.rotate,
                  }}
                  transition={{
                    duration: 0.55,
                    delay: i * 0.05,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <NodeChip {...n} />
                </motion.div>
              ))}
          </AnimatePresence>

          {/* Central element — folder (closed) ↔ hub (open) share ONE
            container, so they keep the exact same position & size. */}
          <div
            className="absolute left-1/2 top-[49%] w-[180px] -translate-x-1/2 -translate-y-1/2"
            style={{ zIndex: 2 }}
          >
            <AnimatePresence mode="wait">
              {open ? (
                <motion.div
                  key="hub"
                  className="relative mx-auto w-[86%]"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Image
                    src="/assets/creativeIntelligence/hub.svg"
                    alt=""
                    width={1000}
                    height={1000}
                    className="w-full"
                  />
                  <Image
                    src="/assets/creativeIntelligence/lock.svg"
                    alt=""
                    width={1000}
                    height={1000}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  />
                </motion.div>
              ) : (
                <motion.button
                  key="folder"
                  type="button"
                  onClick={() => setOpen(true)}
                  aria-label="Open integrations"
                  className="relative block w-full cursor-pointer"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Image
                    src="/assets/creativeIntelligence/folder.svg"
                    alt=""
                    width={1000}
                    height={1000}
                    className="w-full"
                  />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <div className="absolute left-1/2 top-[72%] w-full -translate-x-1/2 px-4 text-center">
            <div className="relative mx-auto min-h-[80px] max-w-[560px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={open ? "open" : "closed"}
                  className="absolute inset-x-0 top-0"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <p className="mx-auto max-w-[560px] text-[20px] font-semibold tracking-[-0.3px] text-[#0F1118] 3xl:text-[22px]">
                    {open
                      ? "Platform integrations. Zero context switching."
                      : "Tap the folder to see your integrations"}
                  </p>
                  <p className="mx-auto mt-2 max-w-[480px] text-[14px] leading-[1.6] text-zinc-800/55 3xl:text-[15px]">
                    {open
                      ? "Insights flow from Imagive into the tools your team already uses."
                      : "All connected. All synced. All in one place."}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-pressed={open}
              className="mx-auto mt-6 inline-flex items-center gap-2.5 rounded-full bg-white px-3.5 py-2 shadow-[0_8px_24px_-10px_rgba(15,17,24,0.35)] ring-1 ring-black/5"
            >
              <span
                className={`relative h-5 w-9 rounded-full transition-colors duration-300 ${
                  open ? "bg-[#6366F1]" : "bg-zinc-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                    open ? "left-[18px]" : "left-0.5"
                  }`}
                />
              </span>
              <span className="text-[14px] font-medium text-[#0F1118]">
                {open ? "Close" : "open"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StackIntegrations;
